/**
 * „Wie ist der Anruf gelaufen?" — der Dialog nach dem Wählen.
 *
 * Bewusst kurz: ein Klick auf das Ergebnis genügt, alles andere ist freiwillig.
 * Ein Formular, das mehr verlangt, wird im Alltag weggeklickt — und dann steht
 * beim nächsten Kontakt wieder nirgends, ob schon jemand angerufen hat.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Chip,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IconAlertTriangle, IconCalendarClock, IconPhoneOff } from '@tabler/icons-react';
import { api } from '../../lib/api';
import type { CallOriginType, CallOutcome } from '../../lib/api';
import { CALL_OUTCOMES, formatCallDuration } from './callOutcomes';

/** Schnellauswahl Wiedervorlage — Berliner Wanduhr, als ISO an den Server. */
function followUpPreset(kind: 'hour' | 'tomorrow' | 'dayAfter'): string {
  if (kind === 'hour') {
    const t = new Date(Date.now() + 60 * 60_000);
    t.setMinutes(Math.ceil(t.getMinutes() / 5) * 5, 0, 0);
    return t.toISOString();
  }
  const d = new Date();
  d.setDate(d.getDate() + (kind === 'tomorrow' ? 1 : 2));
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}

const FOLLOW_UP_PRESETS = [
  { key: 'hour', label: 'In 1 Std.' },
  { key: 'tomorrow', label: 'Morgen 9 Uhr' },
  { key: 'dayAfter', label: 'Übermorgen 9 Uhr' },
] as const;
import type { CallTargetTicket } from './CallButton';

export interface CallOutcomeModalProps {
  opened: boolean;
  onClose: () => void;
  /** Zeitpunkt des Klicks auf „Anrufen" — Grundlage für die gemessene Dauer. */
  startedAt: Date | null;
  phone: string;
  contactName: string | null;
  customerId: string | null;
  /** Kundennummer des führenden Systems, wenn die Portal-Id nicht vorliegt. */
  weclappCustomerId?: string | undefined;
  cwCompanyId?: number | undefined;
  contactId: string | null;
  ticket: CallTargetTicket | null;
  originType?: CallOriginType;
  originId: string | null;
  onLogged?: () => void;
}

export function CallOutcomeModal({
  opened,
  onClose,
  startedAt,
  phone,
  contactName,
  customerId,
  weclappCustomerId,
  cwCompanyId,
  contactId,
  ticket,
  originType,
  originId,
  onLogged,
}: CallOutcomeModalProps) {
  const [outcome, setOutcome] = useState<CallOutcome | null>(null);
  const [note, setNote] = useState('');
  const [followUpAt, setFollowUpAt] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const queryClient = useQueryClient();
  // Die Uhr läuft nur, solange der Dialog offen ist; nach dem Speichern zählt
  // der Wert, der beim Klick auf „Speichern" auf der Uhr stand.
  const frozen = useRef<number | null>(null);

  useEffect(() => {
    if (!opened || !startedAt) return;
    setElapsed(Math.round((Date.now() - startedAt.getTime()) / 1000));
    const timer = setInterval(() => {
      setElapsed(Math.round((Date.now() - startedAt.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [opened, startedAt]);

  useEffect(() => {
    if (opened) {
      setOutcome(null);
      setNote('');
      setFollowUpAt(null);
      frozen.current = null;
    }
  }, [opened]);

  const logMutation = useMutation({
    mutationFn: () =>
      api.logCall({
        phone,
        ...(contactName ? { contactName } : {}),
        ...(customerId ? { customerId } : {}),
        ...(weclappCustomerId ? { weclappCustomerId } : {}),
        ...(cwCompanyId != null ? { cwCompanyId } : {}),
        ...(contactId ? { contactId } : {}),
        ...(ticket ? { ticketProvider: ticket.provider, ticketId: ticket.id } : {}),
        ...(ticket?.number ? { ticketNumber: ticket.number } : {}),
        ...(originType ? { originType } : {}),
        ...(originId ? { originId } : {}),
        outcome: outcome!,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(followUpAt ? { followUpAt } : {}),
        ...(startedAt ? { startedAt: startedAt.toISOString() } : {}),
        ...(frozen.current != null && frozen.current > 0 ? { durationSec: frozen.current } : {}),
      }),
    onSuccess: (result) => {
      onClose();
      if (result.ticketCommentError) {
        // Nicht als Erfolg verkaufen: Der Anruf steht im Protokoll, im Ticket
        // aber nicht — wer sich auf den Vermerk verlässt, sucht ihn sonst dort.
        notifications.show({
          title: 'Anruf gespeichert — Ticket-Vermerk nicht',
          message: `Der Anruf steht im Kundenverlauf. Der interne Vermerk am Ticket ist gescheitert: ${result.ticketCommentError}`,
          color: 'orange',
          autoClose: 12_000,
        });
      } else {
        notifications.show({
          message:
            (ticket ? 'Anruf dokumentiert — Vermerk ist im Ticket' : 'Anruf dokumentiert') +
            (result.followUpTodoId ? '. Die Wiedervorlage erinnert per Push.' : ''),
          color: 'teal',
        });
      }
      if (result.followUpTodoId) void queryClient.invalidateQueries({ queryKey: ['todos'] });
      onLogged?.();
    },
    onError: (err: Error) => {
      notifications.show({ title: 'Nicht gespeichert', message: err.message, color: 'red' });
    },
  });

  const duration = formatCallDuration(elapsed);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Wie ist der Anruf gelaufen?"
      size="md"
      centered
      zIndex={2400}
    >
      <Stack gap="md">
        <Group gap="xs" justify="space-between" wrap="nowrap">
          <div style={{ minWidth: 0 }}>
            <Text fw={600} truncate>
              {contactName ?? phone}
            </Text>
            {contactName && (
              <Text size="xs" c="dimmed" ff="monospace">
                {phone}
              </Text>
            )}
          </div>
          {duration && (
            <Tooltip label={'Läuft seit dem Klick auf „Anrufen"'} withArrow>
              <Badge variant="light" color="gray" ff="monospace">
                {duration}
              </Badge>
            </Tooltip>
          )}
        </Group>

        {ticket && (
          <Text size="xs" c="dimmed">
            Wird als <b>interner</b> Vermerk am Ticket {ticket.number ?? `#${ticket.id}`} hinterlegt —
            der Kunde sieht ihn nicht.
          </Text>
        )}

        <div>
          <Text size="sm" fw={500} mb={6}>
            Ergebnis
          </Text>
          <Group gap="xs">
            {CALL_OUTCOMES.map((o) => (
              <UnstyledButton
                key={o.value}
                onClick={() => setOutcome(o.value)}
                data-testid={`call-outcome-${o.value}`}
              >
                <Badge
                  size="lg"
                  variant={outcome === o.value ? 'filled' : 'light'}
                  color={o.color}
                  style={{ cursor: 'pointer', textTransform: 'none' }}
                >
                  {o.label}
                </Badge>
              </UnstyledButton>
            ))}
          </Group>
        </div>

        <Textarea
          label="Notiz"
          description="Was besprochen wurde — das liest der Nächste, der hier anruft."
          placeholder="z. B. Bondrucker läuft wieder, M1/M2 folgen morgen"
          autosize
          minRows={2}
          maxRows={6}
          maxLength={4000}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />

        <DateTimePicker
          label="Wiedervorlage"
          description={
            originType === 'callback'
              ? 'Optional — landet an der Rückruf-Aufgabe; der Zuständige wird zur Uhrzeit per Push erinnert.'
              : outcome === 'NO_ANSWER' || outcome === 'VOICEMAIL' || outcome === 'BUSY' || outcome === 'CALLBACK_AGREED'
                ? 'Optional — du wirst per Push erinnert. Erreicht ihr die Person vorher (Anruf „erreicht" oder Gespräch laut Telefonanlage), erledigt sich die Erinnerung von selbst.'
                : 'Optional — du wirst zur Uhrzeit per Push erinnert (Aufgaben & Erinnerungen).'
          }
          placeholder="Kein Termin"
          leftSection={<IconCalendarClock size={16} />}
          clearable
          value={followUpAt ? new Date(followUpAt) : null}
          onChange={(val) => setFollowUpAt(val ? new Date(val).toISOString() : null)}
          minDate={new Date()}
          valueFormat="DD.MM.YYYY HH:mm"
          popoverProps={{ zIndex: 2500, withinPortal: true }}
        />
        <Group gap={6} mt={-6}>
          {FOLLOW_UP_PRESETS.map((p) => (
            <Chip key={p.key} size="xs" checked={false} onChange={() => setFollowUpAt(followUpPreset(p.key))}>
              {p.label}
            </Chip>
          ))}
        </Group>

        {!outcome && (
          <Alert color="gray" p="xs" icon={<IconAlertTriangle size={16} />}>
            Ohne Ergebnis lässt sich der Anruf nicht speichern — genau das ist die
            Angabe, die später zählt.
          </Alert>
        )}

        <Group justify="space-between">
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            leftSection={<IconPhoneOff size={14} />}
            onClick={onClose}
          >
            Nicht dokumentieren
          </Button>
          <Button
            size="sm"
            color="teal"
            disabled={!outcome}
            loading={logMutation.isPending}
            onClick={() => {
              frozen.current = elapsed;
              logMutation.mutate();
            }}
          >
            Speichern
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
